import React, { useState, useEffect } from 'react';
import { 
  Shield, Users, Activity, Clock, Search, Filter, RefreshCw,
  FileText, Settings, AlertTriangle, Check, X, Eye, ChevronDown,
  Download, Calendar, User, Loader2, BarChart3, Edit2, Save, Plus,
  Lock, Trash2
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SuperAdminPortal = ({ onBack, user }) => {
  const [activeTab, setActiveTab] = useState('audit');
  const [loading, setLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleForm, setRoleForm] = useState({ name: '', display_name: '', description: '', permissions: [], color: '#2563EB' });
  const [editingRole, setEditingRole] = useState(null);

  const token = localStorage.getItem('auth_token');

  // Fetch audit logs
  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '50');
      params.append('skip', String(page * 50));
      if (searchQuery) params.append('user_email', searchQuery);
      if (actionFilter) params.append('action_type', actionFilter);

      const res = await fetch(`${API_URL}/api/superadmin/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs || []);
        setTotalLogs(data.total || 0);
      } else {
        toast.error('Failed to fetch audit logs');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/users/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Default roles if none from database
  const defaultRoles = [
    { name: 'admin', display_name: 'Administrator', color: '#7C3AED' },
    { name: 'customer', display_name: 'Customer', color: '#2563EB' },
    { name: 'staff', display_name: 'Staff', color: '#059669' },
    { name: 'driver', display_name: 'Driver', color: '#D97706' },
  ];

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const fetchedRoles = data.roles || [];
        // Merge with default roles to ensure core roles are always available
        const roleNames = fetchedRoles.map(r => r.name);
        const mergedRoles = [...fetchedRoles];
        defaultRoles.forEach(dr => {
          if (!roleNames.includes(dr.name)) {
            mergedRoles.push(dr);
          }
        });
        setRoles(mergedRoles);
      } else {
        setRoles(defaultRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoles(defaultRoles);
    }
  };

  // Fetch activity logs
  const fetchActivityLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/activity-logs?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActivityLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  };

  // Update user role
  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        toast.success('User role updated successfully');
        fetchUsers();
        setEditingUserId(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  // Create role
  const createRole = async () => {
    if (!roleForm.name || !roleForm.display_name) {
      toast.error('Name and display name are required');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/superadmin/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(roleForm)
      });
      if (res.ok) {
        toast.success('Role created successfully');
        setShowRoleModal(false);
        setRoleForm({ name: '', display_name: '', description: '', permissions: [], color: '#2563EB' });
        fetchRoles();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create role');
      }
    } catch (error) {
      toast.error('Failed to create role');
    }
  };

  // Update role
  const updateRole = async () => {
    try {
      const res = await fetch(`${API_URL}/api/superadmin/roles/${editingRole.name}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          display_name: roleForm.display_name,
          description: roleForm.description,
          permissions: roleForm.permissions,
          color: roleForm.color
        })
      });
      if (res.ok) {
        toast.success('Role updated successfully');
        setEditingRole(null);
        setShowRoleModal(false);
        setRoleForm({ name: '', display_name: '', description: '', permissions: [], color: '#2563EB' });
        fetchRoles();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  // Delete role
  const deleteRole = async (roleName) => {
    if (!window.confirm(`Delete role "${roleName}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/superadmin/roles/${roleName}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Role deleted');
        fetchRoles();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete role');
      }
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  useEffect(() => {
    fetchAuditLogs();
    fetchUsers();
    fetchRoles();
    fetchActivityLogs();
  }, [page, searchQuery, actionFilter]);

  const actionTypes = [
    { value: '', label: 'All Actions' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'order_created', label: 'Order Created' },
    { value: 'order_updated', label: 'Order Updated' },
    { value: 'product_added', label: 'Product Added' },
    { value: 'product_updated', label: 'Product Updated' },
    { value: 'user_created', label: 'User Created' },
    { value: 'user_updated', label: 'User Updated' },
    { value: 'payment_confirmed', label: 'Payment Confirmed' },
    { value: 'settings_changed', label: 'Settings Changed' },
  ];

  const getActionColor = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-700',
      logout: 'bg-gray-100 text-gray-700',
      order_created: 'bg-blue-100 text-blue-700',
      order_updated: 'bg-purple-100 text-purple-700',
      payment_confirmed: 'bg-emerald-100 text-emerald-700',
      product_added: 'bg-orange-100 text-orange-700',
      product_updated: 'bg-yellow-100 text-yellow-700',
      user_created: 'bg-cyan-100 text-cyan-700',
      settings_changed: 'bg-pink-100 text-pink-700',
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-800 to-purple-900 px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} className="text-white hover:bg-white/20" />
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-7 h-7" />
                  Super Admin Portal
                </h1>
                <p className="text-purple-200 text-sm">System administration and audit controls</p>
              </div>
            </div>
            <Badge className="bg-red-500 text-white border-0 py-1 px-3">
              SUPER ADMIN ONLY
            </Badge>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4 overflow-x-auto">
            {[
              { id: 'audit', label: 'Audit Trail', icon: Activity },
              { id: 'users', label: 'User Management', icon: Users },
              { id: 'roles', label: 'Role Management', icon: Shield },
              { id: 'activity', label: 'Activity Logs', icon: BarChart3 },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-purple-600 text-purple-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by user email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                {actionTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAuditLogs}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalLogs}</p>
                <p className="text-sm text-gray-500">Total Events</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                <p className="text-sm text-gray-500">Total Users</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {auditLogs.filter(l => l.action === 'login').length}
                </p>
                <p className="text-sm text-gray-500">Recent Logins</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {auditLogs.filter(l => l.action?.includes('error')).length}
                </p>
                <p className="text-sm text-gray-500">Errors</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Audit Logs Table */}
        {activeTab === 'audit' && (
          <Card className="overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold text-gray-900">Audit Trail</h2>
              <p className="text-sm text-gray-500">Track all user activities across the system</p>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
                <p className="text-gray-500 mt-2">Loading audit logs...</p>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="p-8 text-center">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No audit logs found</p>
                <p className="text-sm text-gray-400">Activities will be recorded as users interact with the system</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {auditLogs.map((log, index) => (
                      <tr key={log.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-600" />
                            </div>
                            <span className="text-sm text-gray-900">{log.user_email || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs capitalize">
                            {log.user_role || '-'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${getActionColor(log.action)}`}>
                            {log.action?.replace(/_/g, ' ') || '-'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {typeof log.details === 'object' 
                            ? JSON.stringify(log.details).substring(0, 50) + '...'
                            : log.details || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {totalLogs > 50 && (
              <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {page * 50 + 1} - {Math.min((page + 1) * 50, totalLogs)} of {totalLogs}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * 50 >= totalLogs}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* User Activity Tab */}
        {activeTab === 'users' && (
          <Card className="overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">User Management</h2>
                <p className="text-sm text-gray-500">View and manage user roles</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Orders</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{u.name}</p>
                          <p className="text-xs text-gray-500">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingUserId === u.id ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedRole}
                              onChange={(e) => setSelectedRole(e.target.value)}
                              className="text-sm border rounded px-2 py-1"
                            >
                              {roles.map(r => (
                                <option key={r.name} value={r.name}>{r.display_name}</option>
                              ))}
                            </select>
                            <Button size="sm" onClick={() => updateUserRole(u.id, selectedRole)}>
                              <Save className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingUserId(null)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className="capitalize"
                              style={{ 
                                backgroundColor: roles.find(r => r.name === u.role)?.color + '20',
                                borderColor: roles.find(r => r.name === u.role)?.color
                              }}
                            >
                              {u.role}
                            </Badge>
                            {u.role !== 'superadmin' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => {
                                  setEditingUserId(u.id);
                                  setSelectedRole(u.role);
                                }}
                                data-testid={`edit-user-role-${u.id}`}
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {u.status || 'active'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {u.order_count || 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSearchQuery(u.email);
                            setActiveTab('audit');
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Activity
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Role Management Tab */}
        {activeTab === 'roles' && (
          <Card className="overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">Role Management</h2>
                <p className="text-sm text-gray-500">Create and manage user roles</p>
              </div>
              <Button
                onClick={() => {
                  setEditingRole(null);
                  setRoleForm({ name: '', display_name: '', description: '', permissions: [], color: '#2563EB' });
                  setShowRoleModal(true);
                }}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="create-role-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </div>
            <div className="p-4 grid gap-4">
              {roles.map((role) => (
                <div key={role.name} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: role.color || '#6B7280' }}
                    >
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{role.display_name}</h3>
                        {role.is_system && (
                          <Badge variant="outline" className="text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            System
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{role.description || 'No description'}</p>
                      <div className="flex gap-1 mt-1">
                        {(role.permissions || []).slice(0, 3).map(p => (
                          <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>
                        ))}
                        {(role.permissions || []).length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{role.permissions.length - 3}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {role.name !== 'superadmin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingRole(role);
                          setRoleForm({
                            name: role.name,
                            display_name: role.display_name,
                            description: role.description || '',
                            permissions: role.permissions || [],
                            color: role.color || '#2563EB'
                          });
                          setShowRoleModal(true);
                        }}
                        data-testid={`edit-role-${role.name}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                    {!role.is_system && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => deleteRole(role.name)}
                        data-testid={`delete-role-${role.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Activity Logs Tab */}
        {activeTab === 'activity' && (
          <Card className="overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900">All Activity Logs</h2>
                <p className="text-sm text-gray-500">Complete activity history across all users</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchActivityLogs}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {activityLogs.length === 0 ? (
                <div className="p-8 text-center">
                  <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No activity logs found</p>
                </div>
              ) : (
                activityLogs.map((log, index) => (
                  <div key={log.id || index} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Activity className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">{log.user_name || 'Unknown'}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-sm text-gray-500">{log.user_email}</span>
                        </div>
                        <p className="text-sm text-gray-700">
                          <Badge variant="outline" className="mr-2">{log.action?.replace(/_/g, ' ')}</Badge>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <span className="text-gray-500 text-xs">
                              {JSON.stringify(log.details).slice(0, 80)}...
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        {/* Role Modal */}
        {showRoleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">{editingRole ? 'Edit Role' : 'Create Role'}</h2>
                <div className="space-y-4">
                  {!editingRole && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Role ID</label>
                      <Input
                        value={roleForm.name}
                        onChange={(e) => setRoleForm({...roleForm, name: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                        placeholder="e.g., warehouse_manager"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Display Name</label>
                    <Input
                      value={roleForm.display_name}
                      onChange={(e) => setRoleForm({...roleForm, display_name: e.target.value})}
                      placeholder="e.g., Warehouse Manager"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <Input
                      value={roleForm.description}
                      onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                      placeholder="Brief description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Color</label>
                    <div className="flex flex-wrap gap-2">
                      {['#DC2626', '#EA580C', '#D97706', '#16A34A', '#059669', '#0891B2', '#2563EB', '#7C3AED', '#C026D3', '#6B7280'].map(c => (
                        <button
                          key={c}
                          onClick={() => setRoleForm({...roleForm, color: c})}
                          className={`w-8 h-8 rounded-lg ${roleForm.color === c ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button variant="outline" className="flex-1" onClick={() => setShowRoleModal(false)}>Cancel</Button>
                  <Button 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={editingRole ? updateRole : createRole}
                  >
                    {editingRole ? 'Save Changes' : 'Create Role'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPortal;
